import { Badge, Input } from '@iptv/ui';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { adminFetch } from '../lib/api';
import { SortableTh, useTableSort } from '../lib/useTableSort';

interface AuditEntry {
  id: number;
  admin_user_id: number | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

const TARGET_TYPES = ['', 'plan', 'bump', 'coupon', 'site', 'order', 'subscription'];

export function AuditPage() {
  const [actionFilter, setActionFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const audit = useQuery<AuditEntry[]>({
    queryKey: ['admin', 'audit', actionFilter, typeFilter],
    queryFn: () =>
      adminFetch<AuditEntry[]>('/api/v1/admin/audit', {
        query: {
          action: actionFilter.length >= 2 ? actionFilter : undefined,
          target_type: typeFilter || undefined,
          limit: 200,
        },
      }),
  });
  const { sorted, sortKey, sortDir, toggleSort } = useTableSort<AuditEntry>(audit.data, {
    initialKey: 'created_at',
    initialDir: 'desc',
  });

  function toggle(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          placeholder="Filter action (e.g. plan.create)"
          className="w-64"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-md border bg-background px-2 py-1.5 text-sm"
        >
          {TARGET_TYPES.map((t) => (
            <option key={t} value={t}>
              {t || 'All target types'}
            </option>
          ))}
        </select>
        <span className="ml-auto text-xs text-muted-foreground">
          {audit.data?.length ?? 0} entries
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50">
            <tr>
              <th className="w-8 px-3 py-2"></th>
              <SortableTh sortKey="created_at" current={sortKey} dir={sortDir} onSort={toggleSort}>
                When
              </SortableTh>
              <SortableTh
                sortKey="admin_user_id"
                current={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              >
                Admin
              </SortableTh>
              <SortableTh sortKey="action" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Action
              </SortableTh>
              <SortableTh sortKey="target_type" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Target
              </SortableTh>
              <SortableTh sortKey="ip_address" current={sortKey} dir={sortDir} onSort={toggleSort}>
                IP
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {audit.isPending && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {audit.data?.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">
                  No audit entries — every destructive admin action is recorded here.
                </td>
              </tr>
            )}
            {sorted.map((e) => {
              const isOpen = expanded.has(e.id);
              const hasBody = e.before_state || e.after_state;
              return (
                <>
                  <tr
                    key={e.id}
                    className={`border-t ${hasBody ? 'cursor-pointer hover:bg-secondary/30' : ''}`}
                    onClick={() => hasBody && toggle(e.id)}
                  >
                    <td className="px-3 py-2 align-top">
                      {hasBody ? (
                        isOpen ? (
                          <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" />
                        )
                      ) : null}
                    </td>
                    <td className="px-3 py-2 align-top text-xs">
                      {new Date(e.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-muted-foreground">
                      #{e.admin_user_id ?? '—'}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <Badge variant="secondary" className="font-mono">
                        {e.action}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 align-top text-xs">
                      {e.target_type ? `${e.target_type}#${e.target_id}` : '—'}
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-muted-foreground">
                      {e.ip_address ?? '—'}
                    </td>
                  </tr>
                  {isOpen && hasBody && (
                    <tr className="border-t bg-secondary/10">
                      <td></td>
                      <td colSpan={5} className="px-3 py-3">
                        <div className="grid gap-3 md:grid-cols-2">
                          <StatePane label="Before" value={e.before_state} />
                          <StatePane label="After" value={e.after_state} />
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatePane({ label, value }: { label: string; value: Record<string, unknown> | null }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <pre className="max-h-72 overflow-auto rounded-md border bg-background p-2 text-[11px] leading-tight">
        {value ? JSON.stringify(value, null, 2) : '—'}
      </pre>
    </div>
  );
}
